import { getScanAltitude } from "@/hooks/use-walk-placeholder-positions";
import { useAppSelector } from "@/store/store-hooks";
import {
  IElementGenericImgSheet,
  IElementOdometryPath,
} from "@faro-lotv/ielement-types";
import {
  selectIElementWorldMatrix,
  selectIElementWorldPosition,
} from "@faro-lotv/project-source";
import { isEqual } from "es-toolkit";
import { useMemo } from "react";
import { Matrix4, Vector3 } from "three";
import { DEFAULT_PLACEHOLDER_FLOOR_OFFSET } from "@/hooks/use-map-placeholder-positions";
import { useOdometryPathPoses } from "./odometry-path-cache";

/**
 * @returns the trajectory of an odometry path in world space, empty while the trajectory is loading
 * @param path the odometry path to compute the trajectory of
 */
function useOdometryPathWorldPositions(
  path: IElementOdometryPath | undefined,
): Vector3[] {
  const poses = useOdometryPathPoses(path);
  const worldMatrix = useAppSelector(
    selectIElementWorldMatrix(path?.id),
    isEqual,
  );

  return useMemo(() => {
    if (!poses) return [];
    const matrix = new Matrix4().fromArray(worldMatrix);
    return poses.map(({ pos }) =>
      new Vector3(pos.x, pos.y, pos.z).applyMatrix4(matrix),
    );
  }, [poses, worldMatrix]);
}

/**
 * @returns the elevation of a sheet, or undefined if no sheet is given
 * @param sheet the sheet to compute the elevation of
 */
function useSheetElevation(
  sheet: IElementGenericImgSheet | undefined,
): number | undefined {
  return useAppSelector((state) =>
    sheet ? selectIElementWorldPosition(sheet.id)(state)[1] : undefined,
  );
}

/**
 * Counterpart of `useMapPlaceholderPositions` for trajectories stored in an odometry path file.
 *
 * @returns the trajectory of an odometry path, flattened on a sheet
 * @param path the odometry path to compute the trajectory of
 * @param sheetForElevation sheet whose elevation will be used to place the trajectory
 */
export function useOdometryPathMapPositions(
  path: IElementOdometryPath | undefined,
  sheetForElevation: IElementGenericImgSheet | undefined,
): Vector3[] {
  const positions = useOdometryPathWorldPositions(path);
  const sheetElevation = useSheetElevation(sheetForElevation);

  return useMemo(() => {
    if (sheetElevation === undefined) return positions;
    const elevation = sheetElevation + DEFAULT_PLACEHOLDER_FLOOR_OFFSET;
    return positions.map(({ x, z }) => new Vector3(x, elevation, z));
  }, [positions, sheetElevation]);
}

/**
 * Counterpart of `useWalkPlaceholderPositions` for trajectories stored in an odometry path file.
 *
 * @returns the trajectory of an odometry path, adjusted to the elevation used in walk mode
 * @param path the odometry path to compute the trajectory of
 * @param floor the floor which the odometry path belongs to
 */
export function useOdometryPathWalkPositions(
  path: IElementOdometryPath | undefined,
  floor: IElementGenericImgSheet | undefined,
): Vector3[] {
  const positions = useOdometryPathWorldPositions(path);
  const altitude = useAppSelector((state) =>
    path ? getScanAltitude(state.iElements, path) : undefined,
  );
  const floorElevation = useSheetElevation(floor);

  return useMemo(() => {
    if (altitude) {
      return positions.map(({ x, y, z }) => new Vector3(x, y - altitude, z));
    }
    if (floorElevation !== undefined) {
      const elevation = floorElevation + DEFAULT_PLACEHOLDER_FLOOR_OFFSET;
      return positions.map(({ x, z }) => new Vector3(x, elevation, z));
    }
    return positions;
  }, [positions, altitude, floorElevation]);
}
